import React, { useEffect, useState } from 'react';
import { Button, Form, FormGroup, Label, Input, FormText, FormFeedback, CustomInput } from 'reactstrap';
import { useSelector } from 'react-redux';
import { passwordStrengthChecker } from '../../../../core/helper';
import { DEFAULT_PRIVACY_POLICY_URL, DEFAULT_TERMS_OF_USE_URL } from '../../../../core/constants';

export const ResetPasswordWithTokensForm = ({
    accountToken,
    passwordToken,
    listSeniorities,
    resetPasswordWithTokens,
    forgotPassword
}) => {
    const systemCheck = useSelector(state => state?.systemCheck?.data?.data);
    const termsOfUseUrl = systemCheck?.terms_of_use_url || DEFAULT_TERMS_OF_USE_URL;
    const privacyPolicyUrl = systemCheck?.privacy_policy_url || DEFAULT_PRIVACY_POLICY_URL;

    const [inputValues, setInputValues] = useState({
        password: '',
        password_confirmation: '',
        senioritySelect: '',
        termsAndPrivacy: false
    });

    const [formErrors, setFormErrors] = useState({
        password: '',
        password_confirmation: '',
        senioritySelect: '',
        termsAndPrivacy: ''
    });

    const [disabled, setDisabled] = useState(true);
    const strength = passwordStrengthChecker(inputValues.password);

    useEffect(() => {
        setDisabled(!formValid());
    }, [inputValues]);

    const handleInputChange = ({ target }) => {
        const { name, value, type, checked } = target;
        const inputValue = type === 'checkbox' ? checked : value;

        // Validaciones
        switch (name) {
            case "password":
                const passwordError = value.length < 8 ? "Minimum 8 characters required" : '';
                setFormErrors(prev => ({
                    ...prev,
                    password: passwordError
                }));

                if (inputValues.password_confirmation) {
                    const matchPassword = inputValues.password_confirmation === value ? '' : 'Password did not matched';
                    setFormErrors(prev => ({
                        ...prev,
                        password_confirmation: inputValues.password_confirmation?.length < 8 ? "Minimum 8 characters required" : matchPassword
                    }));
                }
                break;

            case "password_confirmation":
                const matchPasswordConfirm = inputValues.password === value ? '' : 'Password did not matched';
                setFormErrors(prev => ({
                    ...prev,
                    password_confirmation: value.length < 8 ? "Minimum 8 characters required" : matchPasswordConfirm
                }));
                break;

            case "senioritySelect":
                setFormErrors(prev => ({
                    ...prev,
                    senioritySelect: value === '' || value === 'Select your option' ? 'Seniority is required' : ''
                }));
                break;

            case "termsAndPrivacy":
                setFormErrors(prev => ({
                    ...prev,
                    termsAndPrivacy: !checked ? 'You must agree to the Terms of Use and Privacy Policy' : ''
                }));
                break;

            default:
                break;
        }

        setInputValues(prev => ({
            ...prev,
            [name]: inputValue
        }));
    };

    const formValid = () => {
        let valid = true;

        Object.values(formErrors).forEach(val => {
            if (val && val.length > 0) {
                valid = false;
            }
        });

        if (!inputValues.password || !inputValues.password_confirmation) {
            valid = false;
        }

        if (!inputValues.senioritySelect || inputValues.senioritySelect === '' || inputValues.senioritySelect === 'Select your option') {
            valid = false;
        }

        if (!inputValues.termsAndPrivacy) {
            valid = false;
        }

        if (inputValues.password !== inputValues.password_confirmation) {
            valid = false;
        }

        if (strength === 'Too weak!') {
            valid = false;
        }

        return valid;
    };

    const onSubmit = (e) => {
        e.preventDefault();
        if (formValid()) {
            resetPasswordWithTokens({
                account_token: accountToken,
                password_token: passwordToken,
                password: inputValues.password,
                password_confirmation: inputValues.password_confirmation,
                seniority_id: inputValues.senioritySelect,
                accept_terms: inputValues.termsAndPrivacy
            });
        }
    };

    return (
        <Form onSubmit={onSubmit} className="form-login">
            <FormGroup className='required mb-3'>
                <Label for="password">Password</Label>
                <Input
                    type="password"
                    name="password"
                    id="password"
                    onChange={handleInputChange}
                    value={inputValues.password}
                    invalid={formErrors.password === '' ? null : true}
                />
                {formErrors.password && <FormFeedback>{formErrors.password}</FormFeedback>}
                {inputValues.password && !formErrors.password &&
                    <FormText className='ml-3'>{strength}</FormText>
                }
            </FormGroup>

            <FormGroup className='required mb-3'>
                <Label for="password_confirmation">Confirm Password</Label>
                <Input
                    type="password"
                    name="password_confirmation"
                    id="password_confirmation"
                    onChange={handleInputChange}
                    value={inputValues.password_confirmation}
                    invalid={formErrors.password_confirmation === '' ? null : true}
                />
                {formErrors.password_confirmation && <FormFeedback>{formErrors.password_confirmation}</FormFeedback>}
            </FormGroup>

            <FormGroup className='required mb-3'>
                <Label for="senioritySelect">Seniority</Label>
                <Input
                    type="select"
                    name="senioritySelect"
                    id="senioritySelect"
                    onChange={handleInputChange}
                    value={inputValues.senioritySelect}
                    invalid={formErrors.senioritySelect === '' ? null : true}
                >
                    <option>Select your option</option>
                    {listSeniorities &&
                        listSeniorities.map(item => (
                            <option key={item.id} value={item.id}>
                                {item.name}
                            </option>
                        ))}
                </Input>
                {formErrors.senioritySelect && <FormFeedback>{formErrors.senioritySelect}</FormFeedback>}
            </FormGroup>

            <FormGroup className='required mb-3'>
                <CustomInput
                    className="styled-checkbox mt-1"
                    type="checkbox"
                    id="termsAndPrivacy"
                    name="termsAndPrivacy"
                    checked={inputValues.termsAndPrivacy}
                    onChange={handleInputChange}
                    label={
                        <>
                            I agree to True Advance's{' '}
                            <a
                                href={termsOfUseUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="text-danger font-weight-semibold"
                            >
                                Terms of Use
                            </a>{' '}
                            and{' '}
                            <a
                                href={privacyPolicyUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="text-danger font-weight-semibold"
                            >
                                Privacy Policy
                            </a>
                        </>
                    }
                />
                {formErrors.termsAndPrivacy && <FormFeedback>{formErrors.termsAndPrivacy}</FormFeedback>}
            </FormGroup>

            {forgotPassword.error && forgotPassword.error.message && (
                <FormText color='danger' className="mb-3">
                    {forgotPassword.error.message}
                </FormText>
            )}

            <Button
                type="submit"
                color="green-light"
                className="py-2 rounded-sm mb-3"
                block
                disabled={disabled}
            >
                Create New Password
            </Button>
        </Form>
    );
};

